import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import { IOption, IPagination } from "../../data/AppInterface";
import {
  deleteIcon,
  editIcon,
  eyeIcon,
  filterIcon,
} from "../../assets/icons/SvgIconList";
import {
  AdvanceSearch,
  ICButton,
  ICConfirmationModal,
  ICTable,
} from "../../core-component/ui";
import { ORDER_BY } from "../../utils/AppConstants";
import MeasurementTypeForm from "./MeasurementTypeForm";
import {
  prepareMessageFromParams,
  toggleStatus,
} from "../../utils/AppFunctions";
import {
  activeInactiveMeasurementType,
  deleteMeasurementType,
  fetchAllMeasurementType,
  getGenderDdl,
  getMeasurementTypes,
  IMeasurementType,
} from "./MeasurementTypeListController";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import { ICDropDown } from "../../core-component/form";

let timer: ReturnType<typeof setTimeout>;

const MeasurementTypeListView = () => {
  const { t } = UseTranslationHook();
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const [advanceSearch, setAdvanceSearch] = useState<string>("");
  const [gender, setGender] = useState<IOption<string>[]>([]);
  const [measurementTypeList, setMeasurementTypeList] = useState<
    IMeasurementType[]
  >([]);
  const [measurementTypeToEdit, setMeasurementTypeToEdit] =
    useState<IMeasurementType>();
  const { pagination, setChangePagination } = UsePaginationHook();
  useEffect(() => {
    fetchMeasurementType({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch, advanceSearch]);

  useEffect(() => {
    fetchGender();
  }, []);

  const fetchMeasurementType = (paginationPlayLoad: IPagination) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      setIsLoading(true);
      await fetchAllMeasurementType(
        setMeasurementTypeList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch,
        advanceSearch,
      );
      setIsLoading(false);
    }, 500);
  };

  const fetchGender = async () => {
    await getGenderDdl(setGender);
  };
  const customActionView = (item: IMeasurementType) => {
    return (
      <div className="flex item-center justify-center w-full">
        <div
          className="size-5 cursor-pointer"
          title={t("view_tooltip_icon")}
          onClick={() => {
            setMeasurementTypeToEdit(item);
            setReadOnly(true);
            setShowForm(true);
          }}
        >
          {eyeIcon}
        </div>

        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("edit_tooltip_icon")}
          onClick={() => {
            setShowForm(true);
            setMeasurementTypeToEdit(item);
          }}
        >
          {editIcon}
        </div>
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("delete_tooltip_icon")}
          onClick={() => {
            setShowDeleteConfirmation(true);
            setMeasurementTypeToEdit(item);
          }}
        >
          {deleteIcon}
        </div>
      </div>
    );
  };

  const handleTypeStatus = async (values: IMeasurementType) => {
    const isStatusChange = await activeInactiveMeasurementType(
      values.id,
      values.is_active === BIT_VALUE.One ? BIT_VALUE.Zero : BIT_VALUE.One,
    );
    if (isStatusChange) {
      setMeasurementTypeList(toggleStatus(measurementTypeList, values.id));
    }
  };

  const handleDelete = async (value: boolean) => {
    if (value && measurementTypeToEdit) {
      const data = await deleteMeasurementType(measurementTypeToEdit.id);
      if (data) {
        fetchMeasurementType(pagination);
      }
    }
    setShowDeleteConfirmation(false);
    setMeasurementTypeToEdit(undefined);
  };

  const handleCloseForm = (status: boolean) => {
    setShowForm(false);
    setReadOnly(false);
    if (measurementTypeToEdit) {
      setMeasurementTypeToEdit(undefined);
    }
    if (status) {
      fetchMeasurementType(pagination);
    }
  };

  const filterView = (
    <div className="w-full grid grid-cols-4">
      <ICDropDown
        placeholder={t("gender")}
        selected={advanceSearch}
        options={gender}
        onSelect={(option) => setAdvanceSearch(option.data_value)}
      />
      <div className="!py-0 ml-4">
        <ICButton onClick={() => setAdvanceSearch("")} className="flex !w-fit">
          <div className="size-5 grid place-self-center">{filterIcon}</div>
          &nbsp;{t("clear")}
        </ICButton>
      </div>
    </div>
  );

  return (
    <div className="w-full overflow-auto scrollbar-remove p-4">
      <AdvanceSearch
        handleOnClickAdd={() => setShowForm(true)}
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        filterNode={filterView}
        searchText={enterpriseSearch}
        refreshTable={() => {
          fetchMeasurementType(pagination);
        }}
      />

      <ICTable<IMeasurementType>
        isLoading={isLoading}
        totalFlex="grid grid-cols-16"
        fields={getMeasurementTypes(customActionView, handleTypeStatus, t)}
        data={measurementTypeList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchMeasurementType({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchMeasurementType({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchMeasurementType({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />

      {showForm && (
        <MeasurementTypeForm
          handleClose={handleCloseForm}
          measurementTypeToEdit={measurementTypeToEdit}
          readOnly={readOnly}
        />
      )}
      {showDeleteConfirmation && measurementTypeToEdit && (
        <ICConfirmationModal
          title={t("delete_measurement_type")}
          message={prepareMessageFromParams(t("delete_message"), [
            ["fieldName", measurementTypeToEdit.type_name],
          ])}
          onClose={(value) => handleDelete(value)}
        />
      )}
    </div>
  );
};

export default MeasurementTypeListView;
